import React, {useEffect, useState} from "react";
import {CustomModal} from "../../../components/CustomModel/CustomModal";
import FeatherIcon from "feather-icons-react";
import {useTranslation} from "react-i18next";
import useAxios from "../../../utils/useAxios";
import {MatchStatusSelect} from "./MatchStatusSelect";
import {SubMatchesPanel} from "./SubMatchesPanel";
import {fetchTeamRoster} from "../../../utils/fetchTeamRoster";
import {fetchSubMatches} from "../../../utils/subMatchApi";

export const MatchDetailsModal = ({ isOpen, onClose, match, tournamentGrade, tournamentId, onSave, is_authorized, isMultiSet }) => {
    const { t } = useTranslation();
    const axios = useAxios();

    const [currentSetIndex, setCurrentSetIndex] = useState(0);
    const [team1Members, setTeam1Members] = useState([]);
    const [team2Members, setTeam2Members] = useState([]);

    const [localMatch, setLocalMatch] = useState(match || {});
    
    // Live Score state fields for current set
    const currentSet = localMatch?.sets?.[currentSetIndex] || {};
    const [isTiebreak, setIsTiebreak] = useState(false);

    useEffect(() => {
        if (match) {
            setLocalMatch(match);
        }
    }, [match]);

    useEffect(() => {
        if (!isOpen || tournamentGrade !== 3 || !is_authorized) return;
        // Guards against an out-of-order response: switching this modal to a
        // different team match before the previous fetch resolves could
        // otherwise show the wrong teams' rosters.
        let cancelled = false;
        const t1Id = typeof localMatch.team_1 === 'object' ? localMatch.team_1?.id : localMatch.team_1;
        const t2Id = typeof localMatch.team_2 === 'object' ? localMatch.team_2?.id : localMatch.team_2;
        Promise.all([fetchTeamRoster(axios, tournamentId, t1Id), fetchTeamRoster(axios, tournamentId, t2Id)]).then(([t1, t2]) => {
            if (cancelled) return;
            setTeam1Members(t1);
            setTeam2Members(t2);
        });
        return () => { cancelled = true; };
        // axios (useAxios()) deliberately excluded - it's a brand new instance
        // every render (see useAxios.js, no memoization), so listing it here
        // reruns this effect - and its own setState - on every single render,
        // an infinite loop (confirmed live: "Maximum update depth exceeded").
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, tournamentGrade, localMatch.team_1, localMatch.team_2, tournamentId, is_authorized]);

    // Sub-matches are a fully separate resource on the live API (see
    // SubMatchesPanel.js's own comment) - fetched here, once, so this modal
    // knows whether to show the old single-pairing/live-scoreboard section
    // below or hand off to SubMatchesPanel instead.
    const [subMatches, setSubMatches] = useState([]);
    useEffect(() => {
        if (!isOpen || tournamentGrade !== 3 || !localMatch.id) {
            setSubMatches([]);
            return;
        }
        let cancelled = false;
        fetchSubMatches(axios, tournamentId, localMatch.id).then(list => {
            if (!cancelled) setSubMatches(list);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, tournamentGrade, localMatch.id, tournamentId]);

    if (!isOpen || !match) return null;

    const isTeamMatch = tournamentGrade === 3;
    const hasSubMatches = isTeamMatch && subMatches.length > 0;

    const handleSaveField = (key, value) => {
        const payload = { [key]: value };
        onSave(payload);
        setLocalMatch(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveSetField = (key, value) => {
        const sets = [...(localMatch.sets || [])];
        if (!sets[currentSetIndex]) {
            sets[currentSetIndex] = { number: currentSetIndex + 1 };
        }
        sets[currentSetIndex][key] = value;
        onSave({ [key]: value }, currentSetIndex);
        setLocalMatch(prev => ({ ...prev, sets }));
    };

    const handlePlayerSelect = (field, playerId) => {
        const payload = { [field]: playerId ? Number(playerId) : null };
        onSave(payload);
        setLocalMatch(prev => ({ ...prev, [field]: payload[field] }));
    };

    const isDoubles = localMatch.is_doubles_match;

    const pointsOptions = [
        { label: "0", value: 0 },
        { label: "15", value: 15 },
        { label: "30", value: 30 },
        { label: "40", value: 40 },
        { label: "A", value: 50 },
    ];

    const gamesOptions = Array.from({length: 21}, (_, i) => ({ label: String(i), value: i }));

    return (
        <CustomModal isOpen={isOpen} onClose={onClose}>
            <form className="modal-form">
                <div className="modal-sticky-header">
                    <h2><FeatherIcon icon="list" /> {t("Match Details")} {localMatch.match_number ? `(M${localMatch.match_number})` : ""}</h2>
                </div>

                <div style={{ padding: "20px" }}>
                    {isTeamMatch && (
                        <SubMatchesPanel
                            parentMatch={localMatch}
                            tournamentId={tournamentId}
                            subMatches={subMatches}
                            setSubMatches={setSubMatches}
                            team1Members={team1Members}
                            team2Members={team2Members}
                            is_authorized={is_authorized}
                            isMultiSet={isMultiSet}
                        />
                    )}

                    {!hasSubMatches && tournamentGrade === 3 && is_authorized && (
                        <div style={{ marginBottom: "20px", background: "var(--light-bg)", padding: "15px", borderRadius: "8px" }}>
                            <h4 style={{ marginBottom: "15px" }}>{t("Participants")}</h4>
                            
                            <div style={{ marginBottom: "15px" }}>
                                <label style={{ marginRight: "15px" }}>
                                    <input 
                                        type="radio" 
                                        checked={!isDoubles} 
                                        onChange={() => handleSaveField('is_doubles_match', false)} 
                                    /> {t("Singles")}
                                </label>
                                <label>
                                    <input 
                                        type="radio" 
                                        checked={isDoubles} 
                                        onChange={() => handleSaveField('is_doubles_match', true)} 
                                    /> {t("Doubles")}
                                </label>
                            </div>

                            <div style={{ display: "flex", gap: "20px" }}>
                                <div style={{ flex: 1 }}>
                                    <strong>{t("Team")} 1</strong>
                                    <div style={{ marginTop: "10px" }}>
                                        <select 
                                            className="search" 
                                            value={localMatch.player_1 || ""} 
                                            onChange={(e) => handlePlayerSelect('player_1', e.target.value)}
                                        >
                                            <option value="">{t("Select Player 1")}</option>
                                            {team1Members.map(p => (
                                                <option key={p.player_id || p.id} value={p.player_id || p.id}>
                                                    {p.first_name} {p.last_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {isDoubles && (
                                        <div style={{ marginTop: "10px" }}>
                                            <select 
                                                className="search" 
                                                value={localMatch.player_3 || ""} 
                                                onChange={(e) => handlePlayerSelect('player_3', e.target.value)}
                                            >
                                                <option value="">{t("Select Player 2")}</option>
                                                {team1Members.map(p => (
                                                    <option key={p.player_id || p.id} value={p.player_id || p.id}>
                                                        {p.first_name} {p.last_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <strong>{t("Team")} 2</strong>
                                    <div style={{ marginTop: "10px" }}>
                                        <select 
                                            className="search" 
                                            value={localMatch.player_2 || ""} 
                                            onChange={(e) => handlePlayerSelect('player_2', e.target.value)}
                                        >
                                            <option value="">{t("Select Player 1")}</option>
                                            {team2Members.map(p => (
                                                <option key={p.player_id || p.id} value={p.player_id || p.id}>
                                                    {p.first_name} {p.last_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {isDoubles && (
                                        <div style={{ marginTop: "10px" }}>
                                            <select 
                                                className="search" 
                                                value={localMatch.player_4 || ""} 
                                                onChange={(e) => handlePlayerSelect('player_4', e.target.value)}
                                            >
                                                <option value="">{t("Select Player 2")}</option>
                                                {team2Members.map(p => (
                                                    <option key={p.player_id || p.id} value={p.player_id || p.id}>
                                                        {p.first_name} {p.last_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {!hasSubMatches && (
                    <div style={{ background: "var(--light-bg)", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                            <ul className="tabs" style={{ margin: 0 }}>
                                {[0, 1, 2].map(idx => (
                                    <li key={idx} className={currentSetIndex === idx ? "active" : ""}>
                                        <a href="#" onClick={(e) => { e.preventDefault(); setCurrentSetIndex(idx); }}>
                                            {t("Set")} {idx + 1}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            
                            {is_authorized && (
                                <div>
                                    <label style={{ marginRight: "10px" }}>
                                        <input type="radio" checked={!isTiebreak} onChange={() => setIsTiebreak(false)} /> {t("Points")}
                                    </label>
                                    <label>
                                        <input type="radio" checked={isTiebreak} onChange={() => setIsTiebreak(true)} /> {t("Tiebreak")}
                                    </label>
                                </div>
                            )}
                        </div>

                        {is_authorized ? (
                            <table style={{ width: "100%", textAlign: "center" }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: "10px" }}>{t("Team")} 1</th>
                                        <th style={{ padding: "10px" }}></th>
                                        <th style={{ padding: "10px" }}>{t("Team")} 2</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ padding: "10px" }}>
                                            <input 
                                                type="radio" 
                                                name="serve" 
                                                checked={currentSet.serving_player === localMatch.player_1 || currentSet.serving_player === localMatch.team_1}
                                                onChange={() => handleSaveSetField('serving_player', localMatch.player_1 || localMatch.team_1)}
                                            /> {t("Serve")}
                                        </td>
                                        <td></td>
                                        <td style={{ padding: "10px" }}>
                                            <input 
                                                type="radio" 
                                                name="serve" 
                                                checked={currentSet.serving_player === localMatch.player_2 || currentSet.serving_player === localMatch.team_2}
                                                onChange={() => handleSaveSetField('serving_player', localMatch.player_2 || localMatch.team_2)}
                                            /> {t("Serve")}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: "10px" }}>
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                                <span>{isTiebreak ? t("Tiebreak") : t("Points")}</span>
                                                <select 
                                                    className="search" 
                                                    style={{ width: "60px", textAlign: "center" }}
                                                    value={isTiebreak ? (currentSet.tiebreak_score_1 ?? "") : (currentSet.point_score_1 ?? "")}
                                                    onChange={(e) => handleSaveSetField(isTiebreak ? 'tiebreak_score_1' : 'point_score_1', e.target.value === "" ? null : Number(e.target.value))}
                                                >
                                                    <option value=""></option>
                                                    {(isTiebreak ? gamesOptions : pointsOptions).map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td>:</td>
                                        <td style={{ padding: "10px" }}>
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                                <select 
                                                    className="search" 
                                                    style={{ width: "60px", textAlign: "center" }}
                                                    value={isTiebreak ? (currentSet.tiebreak_score_2 ?? "") : (currentSet.point_score_2 ?? "")}
                                                    onChange={(e) => handleSaveSetField(isTiebreak ? 'tiebreak_score_2' : 'point_score_2', e.target.value === "" ? null : Number(e.target.value))}
                                                >
                                                    <option value=""></option>
                                                    {(isTiebreak ? gamesOptions : pointsOptions).map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                                <span>{isTiebreak ? t("Tiebreak") : t("Points")}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: "10px" }}>
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                                <span>{t("Games")}</span>
                                                <select 
                                                    className="search" 
                                                    style={{ width: "60px", textAlign: "center" }}
                                                    value={currentSet.game_score_1 ?? ""}
                                                    onChange={(e) => handleSaveSetField('game_score_1', e.target.value === "" ? null : Number(e.target.value))}
                                                >
                                                    <option value=""></option>
                                                    {gamesOptions.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </td>
                                        <td>:</td>
                                        <td style={{ padding: "10px" }}>
                                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                                                <select 
                                                    className="search" 
                                                    style={{ width: "60px", textAlign: "center" }}
                                                    value={currentSet.game_score_2 ?? ""}
                                                    onChange={(e) => handleSaveSetField('game_score_2', e.target.value === "" ? null : Number(e.target.value))}
                                                >
                                                    <option value=""></option>
                                                    {gamesOptions.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </select>
                                                <span>{t("Games")}</span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ textAlign: "center", padding: "20px" }}>
                                <h3>
                                    {currentSet.game_score_1 ?? 0} : {currentSet.game_score_2 ?? 0}
                                </h3>
                                <div>
                                    {isTiebreak ? (
                                        <span>TB: {currentSet.tiebreak_score_1 ?? 0} - {currentSet.tiebreak_score_2 ?? 0}</span>
                                    ) : (
                                        <span>{t("Points")}: {pointsOptions.find(o => o.value === currentSet.point_score_1)?.label || 0} - {pointsOptions.find(o => o.value === currentSet.point_score_2)?.label || 0}</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <strong>{t("Status")}</strong>
                        <MatchStatusSelect 
                            match={localMatch}
                            is_authorized={is_authorized}
                            onSave={(payload) => {
                                onSave(payload);
                                setLocalMatch(prev => ({ ...prev, ...payload }));
                            }}
                        />
                    </div>
                </div>
            </form>
        </CustomModal>
    );
};
